import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import theme, { getTheme } from '../theme';
import { useDarkMode } from '../context/DarkModeContext';

const COMMANDS = [
  { id: 'dashboard', label: 'Go to Dashboard', icon: '🏠', action: '/dashboard', category: 'Navigate', hint: '⌘D' },
  { id: 'tutor', label: 'Ask Cailyn — AI Tutor', icon: '🤖', action: '/tutor', category: 'Navigate', hint: '⌘T' },
  { id: 'review', label: 'Review Queue', icon: '🔁', action: '/review', category: 'Navigate', hint: '⌘R' },
  { id: 'planner', label: 'Open Study Planner', icon: '📅', action: '/planner', category: 'Navigate', hint: '⌘P' },
  { id: 'past-papers', label: 'Browse Past Papers', icon: '📄', action: '/past-papers', category: 'Navigate' },
  { id: 'community', label: 'Community Resources', icon: '🌍', action: '/community', category: 'Navigate' },
  { id: 'scores', label: 'Score Tracker', icon: '📊', action: '/scores', category: 'Navigate' },
  { id: 'analytics', label: 'View Analytics', icon: '📈', action: '/analytics', category: 'Navigate' },
  { id: 'focus', label: 'Focus Room', icon: '🎯', action: '/focus', category: 'Navigate' },
  { id: 'leaderboard', label: 'Leaderboard', icon: '🏆', action: '/leaderboard', category: 'Navigate' },
  { id: 'profile', label: 'My Profile', icon: '👤', action: '/profile', category: 'Navigate' },
  { id: 'feedback', label: 'Submit Feedback', icon: '💬', action: '/feedback', category: 'Navigate' },
  { id: 'changelog', label: "What's New", icon: '✨', action: '/changelog', category: 'Navigate' },
  { id: 'integrations', label: 'Integrations & Imports', icon: '🔗', action: '/integrations', category: 'Navigate' },
  { id: 'help', label: 'Help & Support', icon: '❓', action: '/help', category: 'Navigate', hint: '⌘/' },
];

const CommandPalette = ({ subjects, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const { isDark } = useDarkMode();
  const t = getTheme(isDark);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleGlobalEscape = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleGlobalEscape);
    return () => window.removeEventListener('keydown', handleGlobalEscape);
  }, [onClose]);

  const subjectCommands = (subjects || []).flatMap(s => [
    { id: `tutor-${s.id}`, label: `AI Tutor — ${s.name}`, icon: '🤖', action: `/tutor/${s.id}`, category: 'Subjects' },
    { id: `quiz-${s.id}`, label: `Quiz — ${s.name}`, icon: '🎯', action: `/quiz/${s.id}`, category: 'Subjects' },
    { id: `lesson-${s.id}`, label: `Lessons — ${s.name}`, icon: '📚', action: `/subject/${s.id}`, category: 'Subjects' },
    { id: `diagnostic-${s.id}`, label: `Diagnostic Test — ${s.name}`, icon: '🩺', action: `/diagnostic/${s.id}`, category: 'Subjects' },
    { id: `flashcards-${s.id}`, label: `Flashcards — ${s.name}`, icon: '🃏', action: `/flashcards/${s.id}`, category: 'Subjects' },
  ]);

  const allCommands = [...COMMANDS, ...subjectCommands];
  const filtered = query
    ? allCommands.filter(c => c.label.toLowerCase().includes(query.toLowerCase()) || c.category.toLowerCase().includes(query.toLowerCase()))
    : allCommands;

  const grouped = filtered.reduce((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {});

  const flatFiltered = Object.values(grouped).flat();

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i+1, flatFiltered.length-1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i-1, 0)); }
    if (e.key === 'Enter' && flatFiltered[selectedIdx]) { navigate(flatFiltered[selectedIdx].action); onClose(); }
    if (e.key === 'Escape') onClose();
  };

  return (
    <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.65)',backdropFilter:'blur(8px)',display:'flex',alignItems:'flex-start',justifyContent:'center',zIndex:1000,paddingTop:'12vh',paddingLeft:'16px',paddingRight:'16px',boxSizing:'border-box'}}
      onClick={onClose}>
      <div style={{width:'100%',maxWidth:'580px',backgroundColor:t.surface,border:`1px solid ${t.border}`,borderRadius:'20px',overflow:'hidden',boxShadow:'0 24px 60px rgba(0,0,0,0.25)',animation:'fadeInScale 0.18s ease'}}
        onClick={e => e.stopPropagation()}>
        <div style={{padding:'14px 18px',borderBottom:`1px solid ${t.border}`,display:'flex',alignItems:'center',gap:'12px'}}>
          <span style={{fontSize:'15px',color:t.textTert,fontFamily:'monospace'}}>⌘</span>
          <input ref={inputRef} value={query} onChange={e => { setQuery(e.target.value); setSelectedIdx(0); }}
            onKeyDown={handleKey}
            placeholder="Search pages, subjects, tools..."
            aria-label="Command palette search"
            style={{flex:1,background:'none',border:'none',color:t.text,fontSize:'15px',outline:'none',fontFamily:'inherit'}} />
          <span style={{fontSize:'10px',color:t.textTert,backgroundColor:t.surface2,padding:'3px 8px',borderRadius:'6px',border:`1px solid ${t.border}`,fontFamily:'monospace'}}>ESC</span>
        </div>
        <div style={{maxHeight:'420px',overflowY:'auto'}}>
          {Object.entries(grouped).length === 0 ? (
            <div style={{padding:'40px',textAlign:'center',color:t.textTert,fontSize:'13px'}}>
              <div style={{fontSize:'24px',marginBottom:'8px'}}>🔍</div>
              No results for "{query}"
            </div>
          ) : Object.entries(grouped).map(([category, cmds]) => (
            <div key={category}>
              <div style={{padding:'8px 18px 4px',fontSize:'9px',fontWeight:'800',textTransform:'uppercase',letterSpacing:'0.12em',color:t.textTert}}>{category}</div>
              {cmds.map(cmd => {
                const globalIdx = flatFiltered.findIndex(c => c.id === cmd.id);
                const isSelected = globalIdx === selectedIdx;
                return (
                  <button key={cmd.id}
                    onClick={() => { navigate(cmd.action); onClose(); }}
                    onMouseEnter={() => setSelectedIdx(globalIdx)}
                    style={{width:'100%',display:'flex',alignItems:'center',gap:'12px',padding:'10px 18px',backgroundColor:isSelected?t.goldGlow:'transparent',border:'none',cursor:'pointer',textAlign:'left',transition:'background 0.1s',minHeight:'44px'}}>
                    <span style={{fontSize:'16px',flexShrink:0,width:'24px',textAlign:'center'}}>{cmd.icon}</span>
                    <span style={{fontSize:'13px',fontWeight:isSelected?'600':'500',color:isSelected?t.gold:t.text,flex:1}}>{cmd.label}</span>
                    {cmd.hint && <span style={{fontSize:'10px',color:t.textTert,backgroundColor:t.surface2,padding:'2px 6px',borderRadius:'4px',fontFamily:'monospace'}}>{cmd.hint}</span>}
                    {isSelected && !cmd.hint && <span style={{fontSize:'10px',color:t.textTert,fontFamily:'monospace'}}>↵</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div style={{padding:'10px 18px',borderTop:`1px solid ${t.border}`,display:'flex',gap:'16px',backgroundColor:t.surface2}}>
          {[['↑↓','Navigate'],['↵','Select'],['ESC','Close']].map(([key,label]) => (
            <div key={key} style={{display:'flex',alignItems:'center',gap:'5px'}}>
              <span style={{fontSize:'10px',backgroundColor:t.surface,color:t.textSec,padding:'2px 6px',borderRadius:'4px',border:`1px solid ${t.border}`,fontFamily:'monospace'}}>{key}</span>
              <span style={{fontSize:'10px',color:t.textTert}}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
